/**
 * Shared, process-level cache for Romeo JWT access tokens.
 *
 * Romeo's `/api/token/` endpoint mints a JWT with a ~30 minute TTL. During a
 * `next build` (and within a warm serverless instance at runtime) the same
 * token can safely be reused across many pages/requests instead of minting a
 * fresh one each time. This helper memoises the token at module scope so that:
 *
 *   - Build: ~1 mint per build-worker process instead of one per page.
 *   - Runtime: ~1 mint per warm lambda instance instead of one per request.
 *
 * Caveat: module memory is per-process, so this does NOT guarantee a single
 * global token across all build workers / lambda instances. A globally shared
 * token would require external storage (e.g. Vercel KV / Redis); the in-memory
 * cache already removes the build-time hammering without that complexity.
 *
 * The cache is keyed by `${baseUrl}|${username}` so call sites that use
 * different credentials (e.g. the MAB100 index) cache independently rather than
 * sharing a token that may carry a different scope.
 */

interface CacheEntry {
  access: string
  expiresAt: number
}

interface TokenCache {
  entry: CacheEntry | null
  inFlight: Promise<string> | null
}

// Module-scoped cache. Persists for the life of the process (build worker or
// warm serverless instance).
const caches = new Map<string, TokenCache>()

// Refresh slightly before the real expiry to avoid handing out a token that
// expires mid-request.
const SAFETY_MARGIN_MS = 2 * 60 * 1000

// Used when the JWT `exp` claim can't be decoded. Romeo's TTL is ~30 minutes,
// so 25 minutes leaves a comfortable margin.
const FALLBACK_TTL_MS = 25 * 60 * 1000

interface GetRomeoTokenOptions {
  baseUrl: string
  username: string
  password: string
  timeoutMs?: number
}

/**
 * Decode the `exp` claim (seconds since epoch) from a JWT and return it as a
 * millisecond timestamp. Falls back to now + FALLBACK_TTL_MS when the token
 * can't be parsed.
 */
function getExpiry(access: string, now: number): number {
  try {
    const payload = access.split('.')[1]
    if (!payload) return now + FALLBACK_TTL_MS

    // base64url -> base64
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const json = Buffer.from(base64, 'base64').toString('utf8')
    const { exp } = JSON.parse(json) as { exp?: number }

    if (typeof exp === 'number') {
      return exp * 1000
    }
  } catch {
    // fall through to fallback
  }
  return now + FALLBACK_TTL_MS
}

/**
 * Returns a valid Romeo `access` token, reusing a cached one when possible and
 * de-duplicating concurrent mints. Throws on failure (callers should keep their
 * existing try/catch + graceful-null handling).
 */
export async function getRomeoToken({
  baseUrl,
  username,
  password,
  timeoutMs = 8000,
}: GetRomeoTokenOptions): Promise<string> {
  const key = `${baseUrl}|${username}`
  let cache = caches.get(key)
  if (!cache) {
    cache = { entry: null, inFlight: null }
    caches.set(key, cache)
  }

  // Date.now() is fine here — this runs at request/build time, not inside a
  // workflow script.
  const now = Date.now()

  // 1. Reuse a cached token while it's still comfortably valid.
  if (cache.entry && now < cache.entry.expiresAt - SAFETY_MARGIN_MS) {
    return cache.entry.access
  }

  // 2. If a mint is already in flight, share it rather than minting again.
  if (cache.inFlight) {
    return cache.inFlight
  }

  // 3. Mint a new token and store the in-flight promise so concurrent callers
  //    dedupe onto it.
  const mintPromise = (async () => {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), timeoutMs)

    try {
      const authResponse = await fetch(`${baseUrl}/api/token/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ username, password }),
        signal: controller.signal,
      })

      if (
        !authResponse.ok ||
        !authResponse.headers.get('content-type')?.includes('application/json')
      ) {
        throw new Error(
          `Romeo auth failed: ${authResponse.status} ${authResponse.statusText}`
        )
      }

      const { access } = (await authResponse.json()) as { access?: string }
      if (!access) {
        throw new Error('Romeo auth response missing access token')
      }

      cache.entry = { access, expiresAt: getExpiry(access, Date.now()) }
      return access
    } finally {
      clearTimeout(timeout)
    }
  })()

  cache.inFlight = mintPromise

  try {
    // On failure this rejects and the cache entry is left untouched, so the
    // next caller retries rather than serving a poisoned token.
    return await mintPromise
  } finally {
    cache.inFlight = null
  }
}
